/**
 * Broker Login Step Main Page Component
 *
 * This component combines the BrokerLoginStep form with BrokerLoginInstructions
 * in a single page layout similar to APIDetailsStepMainPage to provide a better
 * user experience with instructions visible alongside the login process.
 *
 * The left section contains the login interface and status, while the right
 * section shows step-by-step instructions and security information.
 */

import React, { useState, useEffect, useCallback, useMemo } from "react";
import { memo } from "react";
import BrokerLoginStep from "./BrokerLoginStep";
import BrokerLoginInstructions from "../Components/BrokerLoginInstructions";

const BrokerLoginStepPage = ({
  accountData,
  selectedBroker,
  isEditMode,
  onLoginSuccess,
  onBack,
  loading: parentLoading,
}) => {
  // State for login instructions from backend
  const [loginInstructions, setLoginInstructions] = useState(null);
  const [additionalParams, setAdditionalParams] = useState(null);
  const [loginStatus, setLoginStatus] = useState("pending");

  // Debug effect to track state changes
  // useEffect(() => {
  //   console.log("BrokerLoginStepPage - State changed:", {
  //     loginInstructions,
  //     additionalParams,
  //     loginStatus,
  //   });
  // }, [loginInstructions, additionalParams, loginStatus]);

  // Memoize the broker object to prevent unnecessary re-renders
  const memoizedBroker = useMemo(() => {
    return selectedBroker;
  }, [selectedBroker]);

  // Handle login status updates from the login step
  const handleLoginStatusChange = useCallback(
    (status, instructions, params) => {
      // console.log("BrokerLoginStepPage - Status change:", {
      //   status,
      //   instructions,
      //   params,
      // });
      setLoginStatus(status);
      if (instructions) {
        // console.log(
        //   "BrokerLoginStepPage - Setting login instructions:",
        //   instructions
        // );
        setLoginInstructions(instructions);
      }
      if (params) {
        // console.log("BrokerLoginStepPage - Setting additional params:", params);
        setAdditionalParams(params);
      }
    },
    []
  );

  // Memoized callback functions to prevent re-renders
  const handleLoginSuccess = useCallback(() => {
    setLoginStatus("success");
    // Small delay to show success state before proceeding
    setTimeout(() => {
      onLoginSuccess();
    }, 1000);
  }, [onLoginSuccess]);

  const handleBack = useCallback(() => {
    onBack();
  }, [onBack]);

  return (
    <div className="w-full">
      {/* Full width container with custom grid layout */}
      <div className="grid grid-cols-1 lg:grid-cols-3 xl:grid-cols-10 gap-4 lg:gap-6 xl:gap-8 max-w-[1600px] mx-auto">
        {/* Left Section - Login Interface (70% on large screens) */}
        <div className="lg:col-span-2 xl:col-span-7">
          <div className="bg-bg-2 rounded-lg shadow-sm dark:shadow-gray-900/30 border border-[var(--divider)] p-4 sm:p-6 lg:p-6 xl:p-6 transition-all hover:shadow-md dark:hover:shadow-gray-900/40 h-fit">
            <div className="border-b border-[var(--divider)] pb-4 mb-6">
              <h3 className="ds-h3 sm: lg: text-fg-1 flex items-center">
                <span className="w-3 h-3 bg-blue-500 rounded-full mr-4"></span>
                Broker Login
              </h3>
              <p className="text-xs sm:text-sm lg:text-sm text-fg-2 mt-2">
                {isEditMode ? "Re-authenticate with " : "Connect to "}
                your {selectedBroker?.display_name || selectedBroker?.name}{" "}
                account
              </p>
            </div>
            <BrokerLoginStep
              accountData={accountData}
              selectedBroker={selectedBroker}
              isEditMode={isEditMode}
              onLoginSuccess={handleLoginSuccess}
              onBack={handleBack}
              loading={parentLoading}
              onStatusChange={handleLoginStatusChange}
            />
          </div>
        </div>

        {/* Right Section - Login Instructions (30% on large screens) */}
        <div className="lg:col-span-1 xl:col-span-3">
          <div className="bg-bg-2 rounded-lg shadow-sm dark:shadow-gray-900/30 border border-[var(--divider)] p-4 sm:p-6 lg:p-6 xl:p-6 sticky top-4 sm:top-6 lg:top-8 transition-all h-fit">
            <div className="border-b border-[var(--divider)] pb-3 mb-4">
              <h3 className="ds-h3 sm: lg: text-fg-1 flex items-center">
                <span className="w-2 h-2 bg-green-500 rounded-full mr-3"></span>
                Instructions
              </h3>
              <p className="text-xs sm:text-sm lg:text-sm text-fg-2 mt-2">
                Step-by-step guide
              </p>
            </div>
            <BrokerLoginInstructions
              brokerName={selectedBroker?.display_name || selectedBroker?.name}
              loginInstructions={loginInstructions}
              additionalParams={additionalParams}
              loginStatus={loginStatus}
              brokerId={selectedBroker?.id}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

// Custom comparison function for React.memo to prevent unnecessary re-renders
const arePropsEqual = (prevProps, nextProps) => {
  // Compare primitive props
  if (
    prevProps.isEditMode !== nextProps.isEditMode ||
    prevProps.loading !== nextProps.loading
  ) {
    return false;
  }

  // Compare selectedBroker by id to avoid deep comparison
  const prevBrokerId = prevProps.selectedBroker?.id;
  const nextBrokerId = nextProps.selectedBroker?.id;
  if (prevBrokerId !== nextBrokerId) {
    return false;
  }

  // Compare accountData by id to avoid deep comparison
  const prevAccountId = prevProps.accountData?.account_id;
  const nextAccountId = nextProps.accountData?.account_id;
  if (prevAccountId !== nextAccountId) {
    return false;
  }

  // Compare function references (these should be stable from parent)
  if (
    prevProps.onLoginSuccess !== nextProps.onLoginSuccess ||
    prevProps.onBack !== nextProps.onBack
  ) {
    return false;
  }

  return true;
};

BrokerLoginStepPage.displayName = "BrokerLoginStepPage";

export default memo(BrokerLoginStepPage, arePropsEqual);
